import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import NfcCard from "@/components/NfcCard";
import PhoneMockup from "@/components/PhoneMockup";

const lineWrap = { hidden: {}, visible: { transition: { staggerChildren: 0.16, delayChildren: 0.5 } } };
const line = {
  hidden: { y: "110%", rotate: 2 },
  visible: { y: "0%", rotate: 0, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="marble-bg relative flex min-h-screen items-center overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-40 top-1/4 h-[600px] w-[600px] rounded-full bg-[#C19D60]/[0.05] blur-[120px]" aria-hidden="true" />

      <div className="mx-auto grid w-full max-w-[1440px] items-center gap-16 px-6 pb-28 pt-36 lg:grid-cols-2 lg:px-12 lg:pt-28">
        <motion.div variants={lineWrap} initial="hidden" animate="visible" className="relative z-10">
          <motion.p
            variants={line}
            data-testid="hero-overline"
            className="mb-8 flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.4em] text-[#C19D60]"
          >
            <span className="h-px w-12 bg-[#C19D60]/60" /> Luxury Digital Studio
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <span className="block overflow-hidden pb-1"><motion.span variants={line} className="block">Trasformiamo il tuo</motion.span></span>
            <span className="block overflow-hidden pb-1"><motion.span variants={line} className="block">locale in un'esperienza</motion.span></span>
            <span className="block overflow-hidden pb-2"><motion.span variants={line} className="gold-text block italic">digitale.</motion.span></span>
          </h1>

          <motion.p variants={line} data-testid="hero-subheadline" className="mt-8 max-w-md text-base font-light leading-relaxed text-[#A3A3A3]">
            Menu digitali, NFC, siti web, contenuti fotografici e video professionali. Tutto ciò che serve per portare la tua attività a un livello superiore.
          </motion.p>

          <motion.div variants={line} className="mt-12 flex flex-wrap items-center gap-5">
            <a
              href="#servizi"
              data-testid="hero-cta-primary"
              className="group relative overflow-hidden rounded-[5px] bg-[#C19D60] px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#050505] transition-colors duration-500"
            >
              <span className="absolute inset-0 origin-left scale-x-0 bg-[#E5CAA4] transition-transform duration-500 ease-out group-hover:scale-x-100" />
              <span className="relative flex items-center gap-3">
                Scopri Recensio <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
              </span>
            </a>
            <a
              href="#contatti"
              data-testid="hero-cta-secondary"
              className="group border-b border-[#C19D60]/40 pb-1 text-[12px] font-medium uppercase tracking-[0.22em] text-[#E5CAA4] transition-colors duration-500 hover:border-[#C19D60] hover:text-[#F8F5F0]"
            >
              Parliamone
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mx-auto mt-6 w-full max-w-[520px] sm:mt-0"
          style={{ perspective: 1200 }}
        >
          <motion.div style={{ rotateX, rotateY }} className="relative" data-testid="hero-visual">
            <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} className="relative z-20 ml-auto w-[64%] rotate-2">
              <NfcCard />
            </motion.div>
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} className="absolute -left-4 bottom-[-64px] z-10 hidden sm:block">
              <PhoneMockup />
            </motion.div>
            <div className="absolute -bottom-10 right-6 z-0 h-24 w-[70%] rounded-full bg-black/80 blur-2xl" aria-hidden="true" />
            {[...Array(7)].map((_, i) => (
              <motion.span
                key={i}
                aria-hidden="true"
                className="absolute h-1 w-1 rounded-full bg-[#E5CAA4]"
                style={{ left: `${12 + i * 13}%`, top: `${8 + (i % 3) * 34}%` }}
                animate={{ opacity: [0.1, 0.9, 0.1], scale: [1, 1.8, 1] }}
                transition={{ duration: 3.5 + i * 0.5, repeat: Infinity, delay: i * 0.4 }}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#servizi"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-[#A3A3A3] md:flex"
      >
        Scorri <ArrowDown size={13} className="animate-bounce text-[#C19D60]" />
      </motion.a>
    </section>
  );
};

export default Hero;
